import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AppHolder, ThemedGlobalStyle } from './settings';
import { LanguageProvider } from './hooks/useLanguageSwitch';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { darkTheme } from './settings/appStyle/theme';

import RootLayout from './components/layout/RootLayout';
import AdminRootLayout from './components/layout/AdminRootLayout';
import { LandingContainer } from './modules/GeneralPages';
import { Dashboard, Login, Tickets } from './modules/UserPages';
import { AdminDashboard, AdminLogin } from './modules/AdminPages';
import TeamMembers from './modules/UserPages/TeamMembers/TeamMembers';
import TicketDetails from './modules/UserPages/TicketDetails/TicketDetails';
import AdminTickets from './modules/AdminPages/AdminTickets/AdminTickets';
import Clients from './modules/AdminPages/Clients/Clients';
import AdminTicketDetails from './modules/AdminPages/AdminTicketDetails/AdminTicketDetails';
import Users from './modules/AdminPages/Users/Users';

const queryClient = new QueryClient();

const getUserRole = () => localStorage.getItem('userRole');

const useAuthStatus = () => {
  const { session } = useAuth();
  const isLoggedIn = Boolean(session?.token);
  const role = getUserRole();

  return {
    isLoggedIn,
    isAdmin: isLoggedIn && role === 'admin',
    isUser: isLoggedIn && role === 'user',
  };
};

const ProtectedRoute = () => {
  const location = useLocation();
  const { isLoggedIn, isUser } = useAuthStatus();

  if (!isLoggedIn) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admin cannot access user routes
  if (!isUser) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

const PublicRoute = () => {
  const { isLoggedIn } = useAuthStatus();

  // Both roles now share "/" as their landing point once logged in — the
  // role check happens inside UserOrPublicRoot below, not here.
  if (isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

const AdminProtectedRoute = () => {
  const location = useLocation();
  const { isLoggedIn, isAdmin } = useAuthStatus();

  if (!isLoggedIn) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // User cannot access admin routes
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

const AdminPublicRoute = () => {
  const { isLoggedIn } = useAuthStatus();

  if (isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

const UserRoutes = () => {
  return (
    <Routes>
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<RootLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="tickets" element={<Tickets />} />
          <Route path="team-members" element={<TeamMembers />} />
          <Route path="ticket/:ticketId" element={<TicketDetails />} />
        </Route>
      </Route>
    </Routes>
  );
};

const AdminRoutes = () => {
  return (
    <Routes>
      <Route element={<AdminProtectedRoute />}>
        <Route path="/" element={<AdminRootLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="tickets" element={<AdminTickets />} />
          <Route path="clients" element={<Clients />} />
          <Route path="users/:clientId" element={<Users />} />
          <Route path="ticket/:ticketId" element={<AdminTicketDetails />} />
        </Route>
      </Route>
    </Routes>
  );
};

const UserOrPublicRoot = () => {
  const { isLoggedIn, isAdmin, isUser } = useAuthStatus();

  if (!isLoggedIn) {
    return (
      <AppHolder id="app-global">
        <LandingContainer />
      </AppHolder>
    );
  }

  if (isAdmin) {
    return (
      <AppHolder id="app-global">
        <AdminRoutes />
      </AppHolder>
    );
  }

  if (isUser) {
    return (
      <AppHolder id="app-global">
        <UserRoutes />
      </AppHolder>
    );
  }

  // Logged in but role is missing/corrupted — treat as logged out
  return <Navigate to="/login" replace />;
};

// ============================================
// App
// ============================================
function App() {
  return (
    <ThemeProvider theme={darkTheme}>
      <ThemedGlobalStyle />

      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <LanguageProvider>
              <Routes>
                <Route
                  path="/policy"
                  element={
                    <AppHolder id="app-global">
                      <LandingContainer />
                    </AppHolder>
                  }
                />

                <Route
                  path="/terms"
                  element={
                    <AppHolder id="app-global">
                      <LandingContainer />
                    </AppHolder>
                  }
                />

                {/* User login — its own path, but content lives at "/" once logged in */}
                <Route element={<PublicRoute />}>
                  <Route
                    path="/login"
                    element={
                      <AppHolder id="app-global">
                        <Login />
                      </AppHolder>
                    }
                  />
                </Route>

                {/* Admin login — its own path, but content ALSO lives at "/" once logged in */}
                <Route element={<AdminPublicRoute />}>
                  <Route
                    path="/admin/login"
                    element={
                      <AppHolder id="app-global">
                        <AdminLogin />
                      </AppHolder>
                    }
                  />
                </Route>

                {/* Everything else — role decides whether this renders
                    Landing, UserRoutes, or AdminRoutes, all under "/" */}
                <Route path="/*" element={<UserOrPublicRoot />} />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </LanguageProvider>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
